import React from 'react';
import { SEO } from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import ServiceHero from '@/components/services/ServiceHero';
import ServiceBenefits from '@/components/services/ServiceBenefits';
import ServiceFAQ from '@/components/services/ServiceFAQ';
import ServiceCTA from '@/components/services/ServiceCTA';
import ServiceSchema from '@/components/services/ServiceSchema';
import ServiceCrossLinks from '@/components/services/ServiceCrossLinks';
import { Wind } from 'lucide-react';

const CarpetCleaningPage = () => {
  const serviceData = {
    title: "Professional Carpet Cleaning in Ventura, CA",
    subtitle: "Deep Clean Technology for Healthier, Fresher Carpets",
    description: "Advanced carpet cleaning services that remove deep-seated dirt, allergens, and stains. Our eco-friendly methods are perfect for Ventura County families who want clean, healthy indoor environments.",
    icon: <Wind className="h-16 w-16 text-ventura-blue" />,
    beforeImage: "/lovable-uploads/41a0177b-af69-4c82-90f1-192ad6d826ce.webp",
    afterImage: "/lovable-uploads/66ba87fc-3ed0-4ed4-a370-6afea398d84c.webp"
  };

  const benefits = [
    "Deep extraction removes embedded dirt",
    "Eliminates allergens and bacteria",
    "Extends carpet life significantly",
    "Fast drying with professional equipment",
    "Safe, non-toxic cleaning solutions",
    "Stain and odor removal specialists"
  ];

  const faqs = [
    {
      question: "How often should I clean my carpets in Ventura County?",
      answer: "We recommend professional carpet cleaning every 12-18 months for most Ventura homes. High-traffic areas or homes with pets may benefit from more frequent cleaning every 6-12 months."
    },
    {
      question: "How long does it take for carpets to dry?",
      answer: "With our professional equipment, most carpets in Ventura's climate dry within 6-8 hours. We use powerful extraction methods to remove maximum moisture during cleaning."
    },
    {
      question: "Do you offer carpet cleaning throughout Ventura County?",
      answer: "Absolutely! We provide carpet cleaning services in Camarillo, Fillmore, Moorpark, Oxnard, Port Hueneme, Santa Paula, Simi Valley, Thousand Oaks, and Ventura."
    }
  ];

  return (
    <>
      <SEO
        title="Carpet Cleaning Ventura CA | Professional Deep Cleaning Services"
        description="Professional carpet cleaning Ventura CA. Deep extraction removes dirt, stains & allergens in Oxnard, Camarillo. Eco-friendly safe methods. Free quotes!"
        keywords="carpet cleaning Ventura CA, rug cleaning Camarillo, upholstery cleaning Thousand Oaks, Ventura County carpet care"
        path="/services/carpet-cleaning"
      />
      <ServiceSchema
        serviceName="Carpet Cleaning"
        description="Professional carpet cleaning Ventura CA. Deep extraction removes dirt, stains & allergens in Oxnard, Camarillo."
        serviceType="Carpet Cleaning"
        areaServed={["Ventura", "Oxnard", "Camarillo", "Thousand Oaks", "Simi Valley", "Fillmore", "Moorpark", "Port Hueneme", "Santa Paula"]}
        additionalServices={["Window Cleaning", "Pressure Washing", "Soft Wash"]}
      />
      
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ServiceHero {...serviceData} />
        
        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">How Often Should You Clean Your Carpets in Ventura County?</h2>
              <p className="text-lg text-gray-600 mb-8 text-center">
                We recommend professional carpet cleaning every 12-18 months for most Ventura homes, or every 6-12 months for high-traffic
                areas and homes with pets. Ventura County's year-round pleasant weather means more outdoor activities and more dirt tracked
                indoors, so our deep-extraction technology removes the embedded particles regular vacuuming can't reach.
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Comprehensive Carpet Services</h3>
                  <p className="text-gray-600 mb-4">
                    Complete carpet care solutions for your Ventura County property:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Deep steam cleaning extraction</li>
                    <li>Stain and spot treatment</li>
                    <li>Odor elimination services</li>
                    <li>Upholstery cleaning</li>
                    <li>Area rug cleaning</li>
                    <li>Pet stain and odor removal</li>
                  </ul>
                </div>
                <div className="before-after-container">
                  <div className="before-after-image">
                    <img 
                      src={serviceData.beforeImage} 
                      alt="Stained carpet before professional cleaning in Ventura CA" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label before-label">Before</span>
                  </div>
                  <div className="before-after-image">
                    <img 
                      src={serviceData.afterImage} 
                      alt="Fresh clean carpet after professional deep cleaning" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label after-label">After</span>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">State-of-the-Art Carpet Cleaning Technology</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Hot Water Extraction</h4>
                    <p className="text-gray-600 mb-4">
                      Our truck-mounted hot water extraction systems deliver superior cleaning power for homes throughout Camarillo, Oxnard, and Thousand Oaks. The combination of heated water, powerful suction, and specialized cleaning solutions removes deep-seated dirt and allergens that regular cleaning can't touch.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Encapsulation Cleaning</h4>
                    <p className="text-gray-600 mb-4">
                      For low-moisture carpet cleaning in Ventura County's humid coastal areas, we offer encapsulation technology that crystallizes dirt particles for easy removal. This method provides excellent results with faster drying times, perfect for busy households and commercial properties.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Specialized Stain and Odor Solutions</h3>
                <p className="text-gray-600 mb-4">
                  Living in Ventura County means dealing with unique staining challenges. From red wine spills during vineyard visits to mud tracked in from hiking trails, we've seen it all. Our specialized stain removal techniques target specific contaminants using the latest cleaning chemistry and application methods.
                </p>
                <p className="text-gray-600 mb-4">
                  Pet odors are a common concern for families in Simi Valley, Moorpark, and throughout Ventura County. Our enzyme-based odor elimination treatments don't just mask smells – they break down odor-causing bacteria at the molecular level, ensuring your carpets smell fresh and clean long after we've finished.
                </p>
                <p className="text-gray-600 mb-6">
                  For homes near agricultural areas like Fillmore and Santa Paula, we understand how dust and pollen can embed deep in carpet fibers. Our deep extraction process removes these allergens, creating a healthier indoor environment for your family, especially important for those with allergies or respiratory sensitivities.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-12">
                <h3 className="text-2xl font-semibold mb-4">Health and Indoor Air Quality Benefits</h3>
                <p className="text-gray-600 mb-4">
                  Regular professional carpet cleaning is essential for maintaining healthy indoor air quality in Ventura County homes. Carpets act as filters, trapping dust, pollen, and other airborne particles that can affect your family's health. Over time, these contaminants accumulate and can be released back into the air with foot traffic.
                </p>
                <p className="text-gray-600">
                  Our deep cleaning process removes these trapped pollutants, dust mites, and allergens, significantly improving your home's air quality. This is particularly beneficial for families with children, elderly residents, or anyone with asthma or allergies. Clean carpets also reduce the spread of bacteria and viruses, contributing to a healthier living environment.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">Protecting Your Carpet Investment</h3>
                <p className="text-gray-600 mb-4">
                  Quality carpeting represents a significant investment in your Ventura County home's comfort and value. Regular professional cleaning extends carpet life by removing abrasive dirt particles that can damage fibers over time. Our gentle yet effective cleaning methods preserve carpet texture and color, keeping your floors looking newer longer.
                </p>
                <p className="text-gray-600">
                  We also offer carpet protection treatments that create an invisible barrier against future stains and spills. This is especially valuable for high-traffic areas and homes with children or pets, providing extra time to clean up accidents before they become permanent stains.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ServiceBenefits benefits={benefits} />
        <ServiceCrossLinks 
          currentService="Carpet Cleaning"
          relatedServices={[
            { name: 'Window Cleaning', path: '/services/window-washing', icon: 'window' },
            { name: 'Pressure Washing', path: '/services/pressure-washing', icon: 'pressure' },
            { name: 'Soft Wash', path: '/services/soft-wash', icon: 'soft' },
          ]}
        />
        <ServiceFAQ faqs={faqs} serviceName="Carpet Cleaning" />
        <ServiceCTA 
          title="Ready for Fresher, Cleaner Carpets?"
          description="Schedule your professional carpet cleaning and experience the difference deep cleaning technology makes for your Ventura County home."
        />
        <Footer />
      </div>
    </>
  );
};

export default CarpetCleaningPage;